import { useState, useEffect, useRef } from 'react';

/**
 * Module-level cache that persists across component mounts/unmounts.
 * Maps URL → 'loaded' | 'error' so we know immediately on remount
 * whether an image was already fetched successfully.
 */
const imageCache = new Map<string, 'loaded' | 'error'>();

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

/**
 * Preload an image into the browser cache via an off-screen Image object.
 * Resolves true on success, false on failure (after retries).
 */
function preloadImage(src: string, retries = MAX_RETRIES): Promise<boolean> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => {
      if (retries > 0) {
        setTimeout(() => {
          preloadImage(src, retries - 1).then(resolve);
        }, RETRY_DELAY_MS);
      } else {
        resolve(false);
      }
    };
    img.src = src;
  });
}

export interface UseCachedImageResult {
  /** Whether the image has loaded successfully (from cache or fresh) */
  loaded: boolean;
  /** Whether all retry attempts failed */
  errored: boolean;
}

/**
 * Hook that loads an image URL with an in-memory cache and automatic retries.
 *
 * - On first mount with a new URL it preloads via an off-screen Image element.
 * - On subsequent mounts with the same URL it returns `loaded: true` immediately
 *   (no flash / no re-fetch).
 * - Failed loads are retried up to MAX_RETRIES times before marking as errored.
 * - Stale URLs are cleaned up if the URL prop changes before loading completes.
 */
export function useCachedImage(url: string | undefined): UseCachedImageResult {
  const cached = url ? imageCache.get(url) : undefined;

  const [loaded, setLoaded] = useState(cached === 'loaded');
  const [errored, setErrored] = useState(cached === 'error');
  const urlRef = useRef(url);

  useEffect(() => {
    urlRef.current = url;

    if (!url) {
      setLoaded(false);
      setErrored(false);
      return;
    }

    const status = imageCache.get(url);
    if (status === 'loaded') {
      setLoaded(true);
      setErrored(false);
      return;
    }

    // Reset for a fresh load attempt (clears previous error state too)
    setLoaded(false);
    setErrored(false);

    preloadImage(url).then(success => {
      // Bail if the URL changed while we were loading
      if (urlRef.current !== url) return;

      if (success) {
        imageCache.set(url, 'loaded');
        setLoaded(true);
      } else {
        imageCache.set(url, 'error');
        setErrored(true);
      }
    });
  }, [url]);

  return { loaded, errored };
}
